'use client';

import { ArrowLeft, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { RpgButton } from '@/features/lobby/ui/RpgButton';

interface LaunchFooterProps {
  onBack: () => void;
  onNext: () => void;
  backLabel: string;
  nextLabel: string;
  nextDisabled?: boolean;
  /** Hide the Next button entirely — Review owns its own hero CTA. */
  hideNext?: boolean;
  /** Hide the back button entirely on mobile when this is the very first
   *  step (`prevStep === null`). The "Back to lobby" copy and the X close
   *  button in the header would otherwise duplicate each other on a
   *  narrow viewport, eating space the Continue button needs to stay
   *  comfortable to tap. Desktop still shows it (room to spare). */
  hideBackOnMobile?: boolean;
  stepPosition?: string;
}

/**
 * Viewport-fixed nav bar. Back + Continue are always visible regardless of
 * scroll position. No reason-text chatter — steps surface validation inline.
 *
 * On the Review step we hide the Next button entirely because the step owns
 * a prominent hero "Launch board" CTA that triggers the warp transition.
 */
export function LaunchFooter({
  onBack,
  onNext,
  backLabel,
  nextLabel,
  nextDisabled,
  hideNext,
  hideBackOnMobile,
  stepPosition,
}: LaunchFooterProps) {
  return (
    <div
      className={cn(
        'shrink-0 border-t border-amber-400/15',
        'bg-gradient-to-t from-[#0b0906]/95 via-[#0b0906]/85 to-[#0b0906]/60',
        'backdrop-blur-xl',
        // `safe-area-inset-bottom` keeps the bar above iOS home-indicator on
        // notched devices. Tightened padding so the bar stays compact on
        // mobile (it sits directly above the 36px LobbyFooter terms strip,
        // and a chunky padding stack pushes the form content too far up).
        'pb-[max(env(safe-area-inset-bottom),0.5rem)]',
      )}
    >
      <div className="mx-auto flex w-full max-w-[1280px] items-center gap-3 px-3 py-2 md:gap-4 md:px-8 md:py-3">
        {/* On the very first step we render `Back to lobby` on desktop and
            an icon-only mobile fallback so the row stays balanced; the X in
            the header already gives mobile users a way out. */}
        {hideBackOnMobile ? (
          <div className="hidden md:block">
            <RpgButton
              tone="silver"
              size="md"
              onClick={onBack}
              iconLeft={<ArrowLeft className="h-3.5 w-3.5" />}
            >
              {backLabel}
            </RpgButton>
          </div>
        ) : (
          <RpgButton
            tone="silver"
            size="md"
            onClick={onBack}
            iconLeft={<ArrowLeft className="h-3.5 w-3.5" />}
          >
            {backLabel}
          </RpgButton>
        )}

        <div className="flex flex-1 items-center justify-center">
          {stepPosition ? (
            <span className="hidden font-mono text-[10px] uppercase tracking-[0.35em] text-amber-100/45 md:inline">
              {stepPosition}
            </span>
          ) : null}
        </div>

        {hideNext ? (
          // Reserve the width on desktop so Back stays in its position and
          // the counter stays centred — this keeps the bar visually
          // balanced. Mobile compresses the layout (no counter shown) so we
          // skip the spacer there.
          <div aria-hidden className="hidden md:block md:w-[120px]" />
        ) : (
          <RpgButton
            tone="gold"
            size="md"
            onClick={onNext}
            disabled={nextDisabled}
            iconRight={<ArrowRight className="h-3.5 w-3.5" />}
            className={cn(nextDisabled && 'opacity-50 pointer-events-none saturate-50')}
          >
            {nextLabel}
          </RpgButton>
        )}
      </div>
    </div>
  );
}
